/**
 * CollectionItemDetail - Redesigned Collection Item Modal
 *
 * Modern, professional edit modal using EditModal with:
 * - Tab-based navigation
 * - Consistent form field styling
 * - Keyboard shortcuts
 * - Unsaved changes detection
 */

import React, { useEffect, useState, useCallback } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import {
	BaseForm,
	NumberBox,
	TextBox,
} from '../../components/base';
import { EditModal, EditPageField } from '../../components/container';
import ComboBoxCollection from '../../components/shared/hoc-collection/ComboBoxCollection';
import {
	CollectionItemModel,
	dfCollectionItemModel,
	validCollectionItemSchema,
} from '../../context/models/collections/CollectionItemModel';
import ComboBoxKeywordSeo from '../../components/shared/hoc-keyword-seo/ComboBoxKeywordSeo';
import { useDispatch } from 'react-redux';
import { actions } from '../../context/stores/rootActions';
import { Layers, Tag, Link as LinkIcon, Info } from 'lucide-react';

type ICollectionItemDetailModalProps = {
	isShow: boolean;
	title: string;
	detailModel?: CollectionItemModel;
	isSaving: boolean | false;
	onClose: () => void;
	onSubmit: (data: CollectionItemModel) => void;
};

const CollectionItemDetail = (props: ICollectionItemDetailModalProps) => {
	const { isShow, title, detailModel, onSubmit, onClose, isSaving } = props;
	const isAddMode = !detailModel;
	const dispatch = useDispatch();
	const [initialValues, setInitialValues] = useState<CollectionItemModel>(dfCollectionItemModel);
	const [keywordSelected, setKeywordSelected] = useState(0);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const keywordData = useEffect(() => {
		dispatch(actions.keywordSeo.getAllRequest());
	}, [dispatch]);

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<CollectionItemModel>({
		resolver: yupResolver(validCollectionItemSchema() as any),
		mode: 'all',
		defaultValues: initialValues,
	});

	const onSubmitHandler = (values: CollectionItemModel) => {
		onSubmit(values);
		setHasUnsavedChanges(false);
	};

	useEffect(() => {
		if (initialValues) {
			reset(initialValues);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValues, reset]);

	useEffect(() => {
		if (detailModel) {
			setInitialValues((prev) => ({ ...prev, ...detailModel }));
			setHasUnsavedChanges(false);
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [detailModel]);

	// Helper to update field and mark as unsaved
	const updateField = useCallback((field: keyof CollectionItemModel, value: any) => {
		setInitialValues((prev) => ({ ...prev, [field]: value }));
		setHasUnsavedChanges(true);
	}, []);

	// Tab content: Collection Info
	const collectionTab = (
		<div className='space-y-4'>
			<EditPageField
				label='Bộ sưu tập'
				required
				error={errors.collectID?.message}
				helpText='Chọn bộ sưu tập chứa mục này'
			>
				<ComboBoxCollection
					name='collectID'
					error={errors.collectID?.message}
					onValueChanged={(value) => updateField('collectID', value)}
					value={initialValues.collectID}
					hasValid={true}
					placeholder='Chọn bộ sưu tập...'
				/>
			</EditPageField>
		</div>
	);

	// Tab content: Keyword Info
	const keywordTab = (
		<div className='space-y-4'>
			<EditPageField
				label='Chọn từ khóa'
				helpText='Chọn từ khóa SEO đã có sẵn'
			>
				<ComboBoxKeywordSeo
					name='name_selected'
					onValueChanged={(value) => {
						setKeywordSelected(value || 0);
					}}
					onDataChanged={(data) => {
						updateField('name', data?.name || '');
						updateField('link', data?.link || '');
					}}
					value={keywordSelected}
					hasValid={false}
					placeholder='Chọn từ khóa...'
				/>
			</EditPageField>

			<EditPageField
				label='Tên từ khóa'
				required
				error={errors.name?.message}
				helpText='Hoặc nhập tên từ khóa mới'
			>
				<TextBox
					name='name'
					error={errors.name?.message}
					onValueChanged={(value) => updateField('name', value)}
					value={initialValues.name}
					hasValid={true}
					placeholder='Nhập tên từ khóa...'
				/>
			</EditPageField>

			<EditPageField
				label='Đường dẫn'
				required
				error={errors.link?.message}
				helpText='URL liên kết đến trang từ khóa'
			>
				<TextBox
					name='link'
					error={errors.link?.message}
					onValueChanged={(value) => updateField('link', value)}
					value={initialValues.link}
					hasValid={true}
					placeholder='https://...'
				/>
			</EditPageField>

			<EditPageField
				label='Số thứ tự'
				error={errors.stt?.message}
				helpText='Thứ tự hiển thị trong danh sách'
			>
				<NumberBox
					name='stt'
					error={errors.stt?.message}
					onValueChanged={(value) => updateField('stt', value)}
					value={initialValues.stt}
					hasValid={true}
				/>
			</EditPageField>
		</div>
	);

	const tabs = [
		{
			id: 'collection',
			label: 'Bộ sưu tập',
			icon: <Layers className='h-4 w-4' />,
			content: collectionTab,
		},
		{
			id: 'keyword',
			label: 'Thông tin từ khóa',
			icon: <Tag className='h-4 w-4' />,
			content: keywordTab,
		},
	];

	return (
		<EditModal
			isOpen={isShow}
			onClose={onClose}
			title={title}
			description={isAddMode ? 'Tạo mới mục bộ sưu tập' : 'Chỉnh sửa mục bộ sưu tập'}
			sizeClass='md'
			tabs={tabs}
			isSaving={isSaving}
			onSave={handleSubmit(onSubmitHandler as any)}
			onCancel={onClose}
			hasUnsavedChanges={hasUnsavedChanges}
			breadcrumbs={[
				{ label: 'Trang chủ', href: '/' },
				{ label: 'Mục bộ sưu tập', href: '/collections-item' },
				{ label: isAddMode ? 'Tạo mới' : detailModel?.name || 'Chỉnh sửa' },
			]}
			status={isAddMode ? 'Tạo mới' : undefined}
			statusVariant='default'
		/>
	);
};

export default CollectionItemDetail;
